import * as twgl from "twgl.js";
import type { PrimitiveNode, SceneNode } from "@/document/model/nodes";
import type { PrimitiveKind } from "@/document/model/primitives";
import { defaultPrimitiveDefinition } from "@/document/model/primitives";
import type { VectorEdge, VectorVertex } from "@/document/model/vectorNetwork";
import { sampleEdgeBezier } from "@/document/operations/vectorGeometry";
import { createDefaultStyle } from "@/document/model/styles";
import type { DropShadowEffect, InnerShadowEffect } from "@/document/model/styles";
import type { Camera } from "./camera";
import type { DisplayClip, DisplayItem, DisplayPaint } from "./scene/displayList";
import type { RenderPreview } from "./overlayTypes";
import type { Rect } from "@/lib/math/rect";
export {
  FRAME_LABEL_HEIGHT_PX,
  FRAME_LABEL_MAX_WIDTH_PX,
  FRAME_LABEL_MIN_WIDTH_PX,
  FRAME_LABEL_OFFSET_Y_PX,
  FRAME_LABEL_PADDING_X_PX,
} from "./frameLabelGeometry";

export const GIZMO_BLUE: [number, number, number, number] = [0.125, 0.749, 1, 1];
export const GIZMO_WHITE: [number, number, number, number] = [1, 1, 1, 1];
export const VECTOR_EDIT_HOVERED_EDGE_COLOR: [number, number, number, number] = [1, 1, 1, 1];
export const VECTOR_EDIT_SELECTED_EDGE_COLOR: [number, number, number, number] = GIZMO_BLUE;
export const SNAP_GUIDE_COLOR: [number, number, number, number] = [1, 0.12, 0.16, 0.95];
export const GIZMO_STROKE_PX = 1;
export const CIRCLE_STROKE_PX = 2;
export const GIZMO_HOVER_DELTA_PX = 2;
export const SELECTION_HANDLE_SIZE_PX = 8;
export const VERTEX_RADIUS_PX = 4;
export const SELECTED_VERTEX_RADIUS_PX = 5;
export const BEZIER_HANDLE_RADIUS_PX = 3.5;
export const SELECTED_BEZIER_HANDLE_RADIUS_PX = 4.5;
export const PATH_PREVIEW_RADIUS_PX = 4.5;
export interface ViewportScreenInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export function documentPointToScreen(
  point: { x: number; y: number },
  canvas: HTMLCanvasElement,
  camera: Camera,
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  return {
    x: rect.left + (point.x - camera.x) * camera.zoom,
    y: rect.top + (point.y - camera.y) * camera.zoom,
  };
}

export function screenPointToDocument(
  point: { x: number; y: number },
  canvas: HTMLCanvasElement,
  camera: Camera,
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  return {
    x: camera.x + (point.x - rect.left) / camera.zoom,
    y: camera.y + (point.y - rect.top) / camera.zoom,
  };
}

export function clampScreenPointToInsets(
  point: { x: number; y: number },
  size: { width: number; height: number },
  canvas: HTMLCanvasElement,
  insets: ViewportScreenInsets,
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const minX = rect.left + insets.left + size.width / 2;
  const maxX = rect.right - insets.right - size.width / 2;
  const minY = rect.top + insets.top + size.height / 2;
  const maxY = rect.bottom - insets.bottom - size.height / 2;
  return {
    x: clampBetween(point.x, minX, maxX),
    y: clampBetween(point.y, minY, maxY),
  };
}

function clampBetween(value: number, min: number, max: number): number {
  if (max < min) {
    return (min + max) / 2;
  }
  return Math.min(max, Math.max(min, value));
}

export function centeredRect(x: number, y: number, size: number) {
  return { x: x - size / 2, y: y - size / 2, width: size, height: size };
}

export function ellipsizeText(context: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (context.measureText(text).width <= maxWidth) {
    return text;
  }

  const ellipsis = "...";
  let low = 0;
  let high = text.length;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (context.measureText(`${text.slice(0, middle)}${ellipsis}`).width <= maxWidth) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }

  return `${text.slice(0, low)}${ellipsis}`;
}

export function rectVertices(rect: { x: number; y: number; width: number; height: number }): number[] {
  return [
    rect.x,
    rect.y,
    rect.x + rect.width,
    rect.y,
    rect.x,
    rect.y + rect.height,
    rect.x,
    rect.y + rect.height,
    rect.x + rect.width,
    rect.y,
    rect.x + rect.width,
    rect.y + rect.height,
  ];
}

export function createFramebufferTileBuffer(gl: WebGL2RenderingContext, vertices: number[], texcoords?: number[]): twgl.BufferInfo {
  return twgl.createBufferInfoFromArrays(gl, {
    position: {
      numComponents: 2,
      data: vertices,
    },
    texcoord: {
      numComponents: 2,
      data: texcoords ?? [0, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1, 0],
    },
  });
}

export function tileTextureCoordsForSubrect(displayBounds: { x: number; y: number; width: number; height: number }, sourceBounds: { x: number; y: number; width: number; height: number }): number[] {
  const u0 = (displayBounds.x - sourceBounds.x) / sourceBounds.width;
  const u1 = u0 + displayBounds.width / sourceBounds.width;
  // Match createFramebufferTileBuffer: world-top → v=1, world-bottom → v=0.
  const vTop = 1 - (displayBounds.y - sourceBounds.y) / sourceBounds.height;
  const vBottom = 1 - (displayBounds.y + displayBounds.height - sourceBounds.y) / sourceBounds.height;
  return [u0, vTop, u1, vTop, u0, vBottom, u0, vBottom, u1, vTop, u1, vBottom];
}

export function circleVertices(x: number, y: number, radius: number, segments = 24): number[] {
  const vertices: number[] = [];
  for (let index = 0; index < segments; index += 1) {
    const start = (index / segments) * Math.PI * 2;
    const end = ((index + 1) / segments) * Math.PI * 2;
    vertices.push(
      x,
      y,
      x + Math.cos(start) * radius,
      y + Math.sin(start) * radius,
      x + Math.cos(end) * radius,
      y + Math.sin(end) * radius,
    );
  }

  return vertices;
}

export function circleRingVertices(x: number, y: number, outerRadius: number, innerRadius: number, segments = 24): number[] {
  const vertices: number[] = [];
  for (let index = 0; index < segments; index += 1) {
    const start = (index / segments) * Math.PI * 2;
    const end = ((index + 1) / segments) * Math.PI * 2;
    const outerStart = { x: x + Math.cos(start) * outerRadius, y: y + Math.sin(start) * outerRadius };
    const outerEnd = { x: x + Math.cos(end) * outerRadius, y: y + Math.sin(end) * outerRadius };
    const innerStart = { x: x + Math.cos(start) * innerRadius, y: y + Math.sin(start) * innerRadius };
    const innerEnd = { x: x + Math.cos(end) * innerRadius, y: y + Math.sin(end) * innerRadius };

    vertices.push(
      outerStart.x,
      outerStart.y,
      outerEnd.x,
      outerEnd.y,
      innerStart.x,
      innerStart.y,
      innerStart.x,
      innerStart.y,
      outerEnd.x,
      outerEnd.y,
      innerEnd.x,
      innerEnd.y,
    );
  }

  return vertices;
}

export function sampleVectorEdge(
  node: Extract<SceneNode, { type: "vectorNetwork" }>,
  edgeId: string,
  steps: number,
): Array<{ x: number; y: number }> {
  const edge = node.network.edges.find(candidate => candidate.id === edgeId);
  if (!edge) {
    return [];
  }

  const from = node.network.vertices.find(vertex => vertex.id === edge.from.vertexId);
  const to = node.network.vertices.find(vertex => vertex.id === edge.to.vertexId);
  if (!from || !to) {
    return [];
  }

  return sampleEdgeBezier(from, to, edge.from.handle, edge.to.handle, steps);
}

export function sampleVectorEdgeFromMap(
  vertexById: Map<string, VectorVertex>,
  edge: VectorEdge,
  steps: number,
): Array<{ x: number; y: number }> {
  const from = vertexById.get(edge.from.vertexId);
  const to = vertexById.get(edge.to.vertexId);
  if (!from || !to) {
    return [];
  }

  return sampleEdgeBezier(from, to, edge.from.handle, edge.to.handle, steps);
}

export function adaptiveVectorEditStepsForEdge(vertices: Map<string, VectorVertex>, edge: VectorEdge, zoom: number): number {
  const from = vertices.get(edge.from.vertexId);
  const to = vertices.get(edge.to.vertexId);
  if (!from || !to || (!edge.from.handle && !edge.to.handle)) {
    return 1;
  }

  const maxDistance = Math.max(
    1,
    edge.from.handle ? distancePointToLine(edge.from.handle, from, to) : 0,
    edge.to.handle ? distancePointToLine(edge.to.handle, from, to) : 0,
  );
  const documentTolerance = Math.max(0.05, 0.35 / Math.max(1, zoom));
  return Math.min(96, Math.max(2, Math.ceil(Math.sqrt(maxDistance / documentTolerance))));
}

export function distancePointToLine(point: { x: number; y: number }, from: { x: number; y: number }, to: { x: number; y: number }): number {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length <= 0.0001) {
    return Math.hypot(point.x - from.x, point.y - from.y);
  }

  return Math.abs((point.x - from.x) * dy - (point.y - from.y) * dx) / length;
}

export function pointsToLineSegments(points: Array<{ x: number; y: number }>): number[] {
  const vertices: number[] = [];
  for (let index = 0; index < points.length - 1; index += 1) {
    const from = points[index]!;
    const to = points[index + 1]!;
    vertices.push(from.x, from.y, to.x, to.y);
  }

  return vertices;
}

export function boundsFromPoints(points: Array<{ x: number; y: number }>): { x: number; y: number; width: number; height: number } {
  if (points.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  let left = points[0]!.x;
  let top = points[0]!.y;
  let right = left;
  let bottom = top;
  for (let index = 1; index < points.length; index += 1) {
    const point = points[index]!;
    left = Math.min(left, point.x);
    top = Math.min(top, point.y);
    right = Math.max(right, point.x);
    bottom = Math.max(bottom, point.y);
  }
  return { x: left, y: top, width: right - left, height: bottom - top };
}

export function draftPrimitive(kind: PrimitiveKind): PrimitiveNode["primitive"] {
  return defaultPrimitiveDefinition(kind);
}

export function verticalTextOffset(boxHeight: number, textHeight: number, align: "TOP" | "CENTER" | "BOTTOM"): number {
  if (align === "CENTER") {
    return Math.max(0, (boxHeight - textHeight) / 2);
  }
  if (align === "BOTTOM") {
    return Math.max(0, boxHeight - textHeight);
  }
  return 0;
}

export function displayItemToTextNode(item: DisplayItem): Extract<SceneNode, { type: "text" }> {
  if (!item.text) {
    throw new Error(`Display item ${item.id} is missing text data.`);
  }

  const text = item.textAutoLayoutFillWidth && item.text.textLayout === "AUTO_WIDTH"
    ? { ...item.text, textLayout: "AUTO_HEIGHT" as const }
    : item.text;

  return {
    id: item.id,
    name: item.id,
    type: "text",
    visible: true,
    locked: false,
    transform: { x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1 },
    bounds: item.bounds,
    style: {
      ...createDefaultStyle("#111827"),
      fills: item.textFill ? [item.textFill] : [],
    },
    text,
  };
}

export function drawTextWithLetterSpacing(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  letterSpacing: number,
): void {
  if (letterSpacing === 0) {
    context.fillText(text, x, y);
    return;
  }

  let cursor = x;
  for (const character of text) {
    context.fillText(character, cursor, y);
    cursor += context.measureText(character).width + letterSpacing;
  }
}

export function documentBoundsToPixelRect(
  bounds: RenderPreview["bounds"],
  camera: Camera,
  resolution: [number, number],
): { x: number; y: number; width: number; height: number } {
  const scale = camera.zoom * camera.devicePixelRatio;
  const left = (bounds.x - camera.x) * scale;
  const top = (bounds.y - camera.y) * scale;
  const right = (bounds.x + bounds.width - camera.x) * scale;
  const bottom = (bounds.y + bounds.height - camera.y) * scale;
  const x1 = Math.max(0, Math.min(resolution[0], Math.floor(Math.min(left, right))));
  const x2 = Math.max(0, Math.min(resolution[0], Math.ceil(Math.max(left, right))));
  const y1 = Math.max(0, Math.min(resolution[1], Math.floor(resolution[1] - Math.max(top, bottom))));
  const y2 = Math.max(0, Math.min(resolution[1], Math.ceil(resolution[1] - Math.min(top, bottom))));
  return {
    x: x1,
    y: y1,
    width: x2 - x1,
    height: y2 - y1,
  };
}

export function flipPixelRows(pixels: Uint8Array, width: number, height: number): Uint8ClampedArray {
  const flipped = new Uint8ClampedArray(new ArrayBuffer(pixels.length));
  const rowSize = width * 4;
  for (let row = 0; row < height; row += 1) {
    const sourceStart = row * rowSize;
    const targetStart = (height - row - 1) * rowSize;
    flipped.set(pixels.subarray(sourceStart, sourceStart + rowSize), targetStart);
  }
  return flipped;
}

export function canvasFillForDisplayPaint(context: CanvasRenderingContext2D, paint: DisplayPaint): string | CanvasGradient {
  if (paint.type === "solid") {
    return rgbaCss(paint.color);
  }

  if (paint.type === "image") {
    return "transparent";
  }

  if (paint.type === "radialGradient") {
    const gradient = context.createRadialGradient(paint.center[0], paint.center[1], 0, paint.center[0], paint.center[1], Math.max(0.0001, paint.radius));
    for (const stop of paint.stops) {
      gradient.addColorStop(stop.position, rgbaCss(stop.color));
    }
    return gradient;
  }

  const gradient = context.createLinearGradient(paint.start[0], paint.start[1], paint.end[0], paint.end[1]);
  for (const stop of paint.stops) {
    gradient.addColorStop(stop.position, rgbaCss(stop.color));
  }
  return gradient;
}

export function displayImageDrawRect(paint: Extract<DisplayPaint, { type: "image" }>): Rect {
  let { x, y, width, height } = paint.imageRect;
  if (paint.flipX) {
    x += width;
    width = -width;
  }
  if (paint.flipY) {
    y += height;
    height = -height;
  }
  return { x, y, width, height };
}

export function canvasFilterForImagePaint(paint: Extract<DisplayPaint, { type: "image" }>): string {
  const exposure = Math.pow(2, clamp(paint.adjustments.exposure, -100, 100) / 100);
  const contrast = 1 + clamp(paint.adjustments.contrast, -100, 100) / 100;
  const saturation = 1 + clamp(paint.adjustments.saturation, -100, 100) / 100;
  return [
    `brightness(${Math.max(0, exposure * 100)}%)`,
    `contrast(${Math.max(0, contrast * 100)}%)`,
    `saturate(${Math.max(0, saturation * 100)}%)`,
  ].join(" ");
}

export function neutralTextureUniforms(): Record<string, unknown> {
  return {
    u_opacity: 1,
    u_brightness: 1,
    u_contrast: 1,
    u_saturation: 1,
    u_clipPointCount: 0,
    u_clipPoints: new Array<number>(128).fill(0),
  };
}

export function applyImageAdjustmentOverlays(context: CanvasRenderingContext2D, paint: Extract<DisplayPaint, { type: "image" }>): void {
  const drawRect = displayImageDrawRect(paint);
  const rect = {
    x: drawRect.width < 0 ? drawRect.x + drawRect.width : drawRect.x,
    y: drawRect.height < 0 ? drawRect.y + drawRect.height : drawRect.y,
    width: Math.abs(drawRect.width),
    height: Math.abs(drawRect.height),
  };
  const temperature = clamp(paint.adjustments.temperature, -100, 100);
  const tint = clamp(paint.adjustments.tint, -100, 100);
  const highlights = clamp(paint.adjustments.highlights, -100, 100);
  const shadows = clamp(paint.adjustments.shadows, -100, 100);

  context.save();
  context.globalCompositeOperation = "source-atop";
  if (temperature !== 0) {
    context.globalAlpha = Math.abs(temperature) / 400;
    context.fillStyle = temperature > 0 ? "#f59e0b" : "#38bdf8";
    context.fillRect(rect.x, rect.y, rect.width, rect.height);
  }

  if (tint !== 0) {
    context.globalAlpha = Math.abs(tint) / 400;
    context.fillStyle = tint > 0 ? "#d946ef" : "#22c55e";
    context.fillRect(rect.x, rect.y, rect.width, rect.height);
  }

  if (highlights !== 0) {
    context.globalCompositeOperation = highlights > 0 ? "screen" : "multiply";
    context.globalAlpha = Math.abs(highlights) / 500;
    context.fillStyle = highlights > 0 ? "#ffffff" : "#94a3b8";
    context.fillRect(rect.x, rect.y, rect.width, rect.height);
  }

  if (shadows !== 0) {
    context.globalCompositeOperation = shadows > 0 ? "screen" : "multiply";
    context.globalAlpha = Math.abs(shadows) / 500;
    context.fillStyle = shadows > 0 ? "#64748b" : "#000000";
    context.fillRect(rect.x, rect.y, rect.width, rect.height);
  }
  context.restore();
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : 0));
}

export function rgbaCss(color: [number, number, number, number]): string {
  return `rgba(${Math.round(color[0] * 255)}, ${Math.round(color[1] * 255)}, ${Math.round(color[2] * 255)}, ${color[3]})`;
}

export function offsetSpreadBounds(bounds: RenderPreview["bounds"], effect: DropShadowEffect | InnerShadowEffect): RenderPreview["bounds"] {
  return {
    x: bounds.x + effect.offsetX - effect.spread,
    y: bounds.y + effect.offsetY - effect.spread,
    width: bounds.width + effect.spread * 2,
    height: bounds.height + effect.spread * 2,
  };
}

export function shadowPoint(x: number, y: number, bounds: RenderPreview["bounds"], effect: DropShadowEffect | InnerShadowEffect): { x: number; y: number } {
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const scaleX = bounds.width === 0 ? 1 : (bounds.width + effect.spread * 2) / bounds.width;
  const scaleY = bounds.height === 0 ? 1 : (bounds.height + effect.spread * 2) / bounds.height;
  return {
    x: cx + (x - cx) * scaleX + effect.offsetX,
    y: cy + (y - cy) * scaleY + effect.offsetY,
  };
}

export function offsetRectBounds(bounds: RenderPreview["bounds"], dx: number, dy: number): RenderPreview["bounds"] {
  return {
    ...bounds,
    x: bounds.x + dx,
    y: bounds.y + dy,
  };
}

export function offsetFlatVertices(vertices: Float32Array, dx: number, dy: number): Float32Array {
  if (dx === 0 && dy === 0) {
    return vertices;
  }
  const offset = new Float32Array(vertices.length);
  for (let index = 0; index < vertices.length; index += 2) {
    offset[index] = vertices[index]! + dx;
    offset[index + 1] = vertices[index + 1]! + dy;
  }
  return offset;
}

function offsetDisplayPaintForCanvas(paint: DisplayPaint, dx: number, dy: number): DisplayPaint {
  if (dx === 0 && dy === 0) {
    return paint;
  }
  if (paint.type === "linearGradient") {
    return {
      ...paint,
      start: [paint.start[0] + dx, paint.start[1] + dy],
      end: [paint.end[0] + dx, paint.end[1] + dy],
    };
  }
  if (paint.type === "radialGradient") {
    return {
      ...paint,
      center: [paint.center[0] + dx, paint.center[1] + dy],
    };
  }
  if (paint.type === "image") {
    return {
      ...paint,
      imageRect: offsetRectBounds(paint.imageRect, dx, dy),
    };
  }
  return paint;
}

export function displayItemForCanvas(item: DisplayItem): DisplayItem {
  const dx = item.positionOffset?.x ?? 0;
  const dy = item.positionOffset?.y ?? 0;
  if (dx === 0 && dy === 0) {
    return item;
  }
  // During move previews bounds/fill/clip already reflect the new position; positionOffset
  // only applies to vertex-based geometry that the GL path shifts in the shader.
  return {
    ...item,
    positionOffset: undefined,
    shadowContours: item.shadowContours?.map(contour => contour.map(point => ({ x: point.x + dx, y: point.y + dy }))),
    vertices: offsetFlatVertices(item.vertices, dx, dy),
  };
}

export function translateDisplayItem(item: DisplayItem, dx: number, dy: number): DisplayItem {
  if (dx === 0 && dy === 0) {
    return item;
  }
  return {
    ...item,
    positionOffset: undefined,
    bounds: offsetRectBounds(item.bounds, dx, dy),
    visualBounds: item.visualBounds ? offsetRectBounds(item.visualBounds, dx, dy) : undefined,
    clip: item.clip ? offsetDisplayClip(item.clip, dx, dy) : undefined,
    rotationOrigin: item.rotationOrigin ? { x: item.rotationOrigin.x + dx, y: item.rotationOrigin.y + dy } : undefined,
    shadowContours: item.shadowContours?.map(contour => contour.map(point => ({ x: point.x + dx, y: point.y + dy }))),
    vertices: offsetFlatVertices(item.vertices, dx, dy),
    fill: offsetDisplayPaintForCanvas(item.fill, dx, dy),
  };
}

function offsetDisplayClip(clip: DisplayClip, dx: number, dy: number): DisplayClip {
  return {
    bounds: offsetRectBounds(clip.bounds, dx, dy),
    polygon: clip.polygon.map(point => ({ x: point.x + dx, y: point.y + dy })),
  };
}

export function drawShadowShapeMask(
  context: CanvasRenderingContext2D,
  item: {
    bounds: RenderPreview["bounds"];
    vertices: Float32Array | number[];
    shadowContours?: Array<Array<{ x: number; y: number }>>;
  },
  effect: DropShadowEffect | InnerShadowEffect,
): void {
  if (item.shadowContours && item.shadowContours.length > 0) {
    context.beginPath();
    for (const contour of item.shadowContours) {
      if (contour.length < 3) {
        continue;
      }
      const first = shadowPoint(contour[0]!.x, contour[0]!.y, item.bounds, effect);
      context.moveTo(first.x, first.y);
      for (let index = 1; index < contour.length; index += 1) {
        const point = shadowPoint(contour[index]!.x, contour[index]!.y, item.bounds, effect);
        context.lineTo(point.x, point.y);
      }
      context.closePath();
    }
    context.fill("evenodd");
    return;
  }

  context.beginPath();
  for (let index = 0; index < item.vertices.length; index += 6) {
    const a = shadowPoint(item.vertices[index]!, item.vertices[index + 1]!, item.bounds, effect);
    const b = shadowPoint(item.vertices[index + 2]!, item.vertices[index + 3]!, item.bounds, effect);
    const c = shadowPoint(item.vertices[index + 4]!, item.vertices[index + 5]!, item.bounds, effect);
    context.moveTo(a.x, a.y);
    context.lineTo(b.x, b.y);
    context.lineTo(c.x, c.y);
    context.closePath();
  }
  context.fill();
}

export function roundedRectPath(
  context: CanvasRenderingContext2D,
  bounds: RenderPreview["bounds"],
  radii: [number, number, number, number],
): void {
  const normalizedRadii = radii.map(radius => Math.max(0, Math.min(radius, Math.abs(bounds.width) / 2, Math.abs(bounds.height) / 2)));
  const tl = normalizedRadii[0] ?? 0;
  const tr = normalizedRadii[1] ?? 0;
  const br = normalizedRadii[2] ?? 0;
  const bl = normalizedRadii[3] ?? 0;
  const x = bounds.x;
  const y = bounds.y;
  const right = bounds.x + bounds.width;
  const bottom = bounds.y + bounds.height;
  context.beginPath();
  context.moveTo(x + tl, y);
  context.lineTo(right - tr, y);
  context.quadraticCurveTo(right, y, right, y + tr);
  context.lineTo(right, bottom - br);
  context.quadraticCurveTo(right, bottom, right - br, bottom);
  context.lineTo(x + bl, bottom);
  context.quadraticCurveTo(x, bottom, x, bottom - bl);
  context.lineTo(x, y + tl);
  context.quadraticCurveTo(x, y, x + tl, y);
  context.closePath();
}

export function gradientUniformPositions(stops: Array<{ position: number }>): number[] {
  const positions = new Array<number>(32).fill(0);
  stops.slice(0, 32).forEach((stop, index) => {
    positions[index] = stop.position;
  });
  return positions;
}

export function gradientUniformColors(colors: Array<[number, number, number, number]>): number[] {
  const packedColors = new Array<number>(32 * 4).fill(0);
  colors.slice(0, 32).forEach((color, index) => {
    packedColors[index * 4] = color[0];
    packedColors[index * 4 + 1] = color[1];
    packedColors[index * 4 + 2] = color[2];
    packedColors[index * 4 + 3] = color[3];
  });
  return packedColors;
}

export function formatCacheNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(3);
}
